import { Map as LeafletMap } from 'leaflet'
import { FC } from 'react'
import {
  AttributionControl,
  MapContainer,
  ScaleControl,
  ZoomControl,
} from 'react-leaflet'

import { BaseLayerControl } from './base-layer-control'
import CentralizationControl from './centralization-control'

type ControlPosition = 'topleft' | 'topright' | 'bottomleft' | 'bottomright'

type DisabledKeys =
  | 'attributionControl'
  | 'scrollWheelZoom'
  | 'doubleClickZoom'
  | 'dragging'
  | 'zoomControl'
  | 'centralizationControl'
  | 'scaleControl'
  | 'baseLayerControl'

type DisabledControls = {
  [K in DisabledKeys]?: boolean
}

type PositionableControls = {
  zoomControl?: ControlPosition
  centralizationControl?: ControlPosition
  scaleControl?: ControlPosition
  attributionControl?: ControlPosition
  baseLayerControl?: ControlPosition
}

type MapProps = {
  bounds?: L.LatLngBounds
  maxBounds?: L.LatLngBounds
  disabledBaseLayer?: boolean
  disabledControls?: boolean | DisabledControls
  positionControls?: PositionableControls
  preferCanvas?: boolean
  zoomSnap?: number
  markerZoomAnimation?: boolean
  whenCreated?: (map: LeafletMap) => void
  opacity?: number
}

export const Map: FC<MapProps> = ({
  children,
  bounds,
  maxBounds,
  disabledBaseLayer = false,
  disabledControls = false,
  positionControls = {},
  preferCanvas = false,
  whenCreated,
  zoomSnap,
  markerZoomAnimation,
  opacity,
}) => {
  const disabledControlsObject =
    typeof disabledControls === 'object'
      ? {
          attributionControl: !disabledControls.attributionControl,
          zoomControl: false,
          dragging: !disabledControls.dragging,
          doubleClickZoom: !disabledControls.doubleClickZoom,
          scrollWheelZoom: !disabledControls.scrollWheelZoom,
          centralizationControl: disabledControls.centralizationControl,
          scaleControl: disabledControls.scaleControl,
          baseLayerControl: disabledControls.baseLayerControl,
        }
      : {
          attributionControl: !disabledControls,
          zoomControl: false,
          dragging: !disabledControls,
          doubleClickZoom: !disabledControls,
          scrollWheelZoom: !disabledControls,
          centralizationControl: disabledControls,
          scaleControl: disabledControls,
          baseLayerControl: disabledControls,
        }

  const controlPositions = {
    zoomControl: positionControls.zoomControl || 'topleft',
    centralizationControl: positionControls.centralizationControl || 'topleft',
    scaleControl: positionControls.scaleControl || 'bottomright',
    attributionControl: positionControls.attributionControl || 'bottomright',
    baseLayerControl: positionControls.baseLayerControl || 'topright',
  }

  return (
    <MapContainer
      {...disabledControlsObject}
      bounds={bounds}
      preferCanvas={preferCanvas}
      maxBounds={maxBounds}
      whenCreated={whenCreated}
      zoomSnap={zoomSnap}
      markerZoomAnimation={markerZoomAnimation}
    >
      {!(typeof disabledControls === 'object'
        ? disabledControls.zoomControl
        : disabledControls) && (
        <ZoomControl position={controlPositions.zoomControl} />
      )}

      {!disabledControlsObject.centralizationControl && (
        <CentralizationControl
          bounds={bounds}
          position={controlPositions.centralizationControl}
        />
      )}

      {!disabledControlsObject.scaleControl && (
        <ScaleControl position={controlPositions.scaleControl} />
      )}

      {!disabledBaseLayer && (
        <BaseLayerControl
          disabledControls={disabledControlsObject.baseLayerControl}
          opacity={opacity}
        />
      )}

      {!disabledControlsObject.attributionControl && (
        <AttributionControl position={controlPositions.attributionControl} />
      )}

      {children}
    </MapContainer>
  )
}
